//sidecarEngine.ts — 2× upscale through mirror-upscaled, the local VideoToolbox helper.
//Apple's VTLowLatencySuperResolutionScaler is native-only, so the frame leaves the page: each
//rAF we capture the video, ship RGBA8 over a loopback WebSocket, and route the reply through
//the shader finisher — display-grid resample + RCAS + grain — so the AI output lands on real
//pixels instead of a browser bilinear rescale. The helper answers a 720×1280 frame in ~2.6ms;
//the round trip is dominated by moving 18 MB per frame, not by the scaler.
//
//Reachability caveat, not solved here: ws://127.0.0.1 is a non-secure origin, so an https page
//blocks it as mixed content, and Chrome's Private Network Access gates even http pages that
//reach loopback. This path is for the http://localhost kiosk build; a packaged host would need
//the helper to serve wss:// with a trusted certificate.

export interface UpscaleEngine {
	readonly label: string
	render(video: HTMLVideoElement): void
	dispose(): void
}

import { Upscaler } from '../ui/upscaler'

const ENDPOINT = 'ws://127.0.0.1:8765'
const LABEL = 'sidecar (VT SR + shader finish)'
const HANDSHAKE_TIMEOUT_MS = 1500
//A reply that never lands must not wedge the engine forever; after this we allow a new send.
const MAX_CONSECUTIVE_ERRORS = 30
const REPLY_TIMEOUT_MS = 1000
const REQUEST_MAGIC = 0x4d555031
const RESPONSE_MAGIC = 0x4d555032
const HEADER_BYTES = 16

export async function createSidecarEngine(canvas: HTMLCanvasElement): Promise<UpscaleEngine> {
	//The finisher owns the target canvas (webgl2) and applies the display-grid polish pass.
	const finisher = new Upscaler(canvas)
	const capture = document.createElement('canvas')
	//willReadFrequently keeps the capture surface on the CPU; without it every getImageData
	//pays a GPU readback stall.
	const captureContext = capture.getContext('2d', { willReadFrequently: true })
	if (!captureContext) throw new Error('Sidecar upscale unavailable: could not create a capture context')
	let socket: WebSocket
	try {
		socket = await connect()
	} catch (cause) {
		finisher.dispose()
		throw cause
	}
	return new SidecarEngine(finisher, capture, captureContext, socket)
}

function connect(): Promise<WebSocket> {
	return new Promise((resolve, reject) => {
		let socket: WebSocket
		try {
			socket = new WebSocket(ENDPOINT)
		} catch (error) {
			reject(new Error(`Sidecar upscale unavailable: the browser refused ${ENDPOINT} (${describe(error)})`))
			return
		}
		socket.binaryType = 'arraybuffer'
		let timer = 0

		const detach = () => {
			window.clearTimeout(timer)
			socket.removeEventListener('open', onOpen)
			socket.removeEventListener('message', onMessage)
			socket.removeEventListener('error', onLost)
			socket.removeEventListener('close', onLost)
		}
		const fail = (message: string) => {
			detach()
			socket.close()
			reject(new Error(message))
		}
		const onOpen = () => socket.send(JSON.stringify({ type: 'hello' }))
		const onLost = () =>
			fail(`Sidecar upscale unavailable: nothing is listening on ${ENDPOINT} — start the mirror-upscaled helper`)
		const onMessage = (event: MessageEvent) => {
			if (!isReady(event.data)) {
				fail(`Sidecar upscale unavailable: ${ENDPOINT} did not answer hello with a ready message`)
				return
			}
			detach()
			resolve(socket)
		}

		timer = window.setTimeout(
			() => fail(`Sidecar upscale unavailable: ${ENDPOINT} did not answer hello within ${HANDSHAKE_TIMEOUT_MS}ms`),
			HANDSHAKE_TIMEOUT_MS,
		)
		socket.addEventListener('open', onOpen)
		socket.addEventListener('message', onMessage)
		socket.addEventListener('error', onLost)
		socket.addEventListener('close', onLost)
	})
}

function isReady(data: unknown): boolean {
	if (typeof data !== 'string') return false
	try {
		const payload: unknown = JSON.parse(data)
		return typeof payload === 'object' && payload !== null && (payload as { type?: unknown }).type === 'ready'
	} catch {
		return false
	}
}

class SidecarEngine implements UpscaleEngine {
	readonly label = LABEL
	private request: ArrayBuffer | null = null
	private requestBytes: Uint8Array = new Uint8Array(0)
	private incoming: ImageData | null = null
	private inFlight = false
	private sentAt = 0
	private frameId = 0
	private alive = true
	private consecutiveErrors = 0
	private lastError = ''
	private rttMs = 0
	private dropped = 0

	constructor(
		private readonly finisher: Upscaler,
		private readonly capture: HTMLCanvasElement,
		private readonly captureContext: CanvasRenderingContext2D,
		private readonly socket: WebSocket,
	) {
		socket.addEventListener('message', this.onMessage)
		socket.addEventListener('close', this.onLost)
		socket.addEventListener('error', this.onLost)
	}

	render(video: HTMLVideoElement): void {
		//Persistent failure surfaces as a throw so the caller can fall back to another
		//engine instead of leaving a black or frozen canvas on the mirror.
		if (!this.alive) {
			throw new Error('Sidecar connection lost')
		}
		if (this.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
			throw new Error(`Sidecar keeps failing: ${this.lastError || 'unknown helper error'}`)
		}
		this.paintLatest()

		const width = video.videoWidth
		const height = video.videoHeight
		if (!width || !height || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return
		//Latest-frame-wins: never more than one frame on the wire. A frame captured now and sent
		//three frames later would be worse than the frame we skip.
		if (this.inFlight && performance.now() - this.sentAt < REPLY_TIMEOUT_MS) {
			this.dropped += 1
			return
		}
		this.send(video, width, height)
	}

	stats(): string {
		return `sidecar rtt ${this.rttMs.toFixed(1)} ms · skipped ${this.dropped}`
	}

	dispose(): void {
		this.alive = false
		this.incoming = null
		this.finisher.dispose()
		this.socket.removeEventListener('message', this.onMessage)
		this.socket.removeEventListener('close', this.onLost)
		this.socket.removeEventListener('error', this.onLost)
		this.socket.close()
	}

	private send(video: HTMLVideoElement, width: number, height: number): void {
		const total = HEADER_BYTES + width * height * 4
		//Reused across frames, and safe to reuse: the reply only arrives once the helper has read
		//the whole message, so nothing is still reading this buffer by the time we refill it.
		if (!this.request || this.request.byteLength !== total) {
			this.request = new ArrayBuffer(total)
			this.requestBytes = new Uint8Array(this.request, HEADER_BYTES)
		}
		if (this.capture.width !== width || this.capture.height !== height) {
			this.capture.width = width
			this.capture.height = height
		}

		//v1 capture path. VideoFrame.copyTo() is the faster one: it skips the canvas round trip
		//and writes planes straight into a buffer, saving a draw plus a 3.7 MB readback copy.
		this.captureContext.drawImage(video, 0, 0)
		this.requestBytes.set(this.captureContext.getImageData(0, 0, width, height).data)

		this.frameId = (this.frameId + 1) >>> 0
		const header = new DataView(this.request, 0, HEADER_BYTES)
		header.setUint32(0, REQUEST_MAGIC, true)
		header.setUint32(4, this.frameId, true)
		header.setUint32(8, width, true)
		header.setUint32(12, height, true)

		try {
			this.socket.send(this.request)
		} catch {
			this.onLost()
			return
		}
		this.inFlight = true
		this.sentAt = performance.now()
	}

	private paintLatest(): void {
		const frame = this.incoming
		if (!frame) return
		//Kept, not cleared: repainting every rAF lets the grain animate and survives the
		//gaps between helper replies.
		this.finisher.renderSource(frame, frame.width, frame.height)
	}

	private readonly onMessage = (event: MessageEvent): void => {
		const data: unknown = event.data
		//Text replies are the helper reporting a per-frame failure. Clear the in-flight marker
		//so the next rAF tries again — and count them, so a helper that fails every frame
		//trips the fallback in render() instead of showing black forever.
		if (!(data instanceof ArrayBuffer) || data.byteLength < HEADER_BYTES) {
			this.inFlight = false
			this.consecutiveErrors += 1
			if (typeof data === 'string') {
				try {
					const parsed = JSON.parse(data) as { message?: unknown }
					if (typeof parsed.message === 'string') this.lastError = parsed.message
				} catch {
					this.lastError = data.slice(0, 200)
				}
			}
			return
		}
		const header = new DataView(data, 0, HEADER_BYTES)
		if (header.getUint32(0, true) !== RESPONSE_MAGIC) return
		this.inFlight = false
		this.consecutiveErrors = 0
		//Exponential moving average keeps the HUD readable.
		const rtt = performance.now() - this.sentAt
		this.rttMs = this.rttMs === 0 ? rtt : this.rttMs * 0.9 + rtt * 0.1
		const width = header.getUint32(8, true)
		const height = header.getUint32(12, true)
		if (!width || !height || data.byteLength !== HEADER_BYTES + width * height * 4) return
		this.incoming = new ImageData(new Uint8ClampedArray(data, HEADER_BYTES), width, height)
	}

	private readonly onLost = (): void => {
		this.alive = false
		this.incoming = null
	}
}

function describe(error: unknown): string {
	return error instanceof Error ? error.message : String(error)
}
